import React from 'react';
import { createRoot } from 'react-dom/client';

// komponentit
import AutoLoadImagesSetting from '@paxsudos/upload-by-url/AutoLoadImagesSetting';
import AutoAttachByUrl       from '@paxsudos/upload-by-url/auto_attach_by_url';
import StatusActionBar       from '@paxsudos/frontend/status_action_bar';

// per-viesti override-API
import { getEffective, setOverride, clearOverride } from '@paxsudos/upload-by-url/auto_load_prefs';

// ----------------------------------------------------

const DEBUG = true;

function dlog(...args) {
  if (DEBUG) console.info('[autoload]', ...args);
}

// suojataan tuplakäynnistykseltä
if (!window.__AUTOLOAD_BOOT) {
  window.__AUTOLOAD_BOOT = 1;
  dlog('boot');
}

/* ---------------- utils ---------------- */

const pathname = () =>
  (window.location && window.location.pathname) ? window.location.pathname : '/';

const isSettingsPage = () => {
  const p = pathname();
  return p === '/settings' || p.startsWith('/settings') || p.startsWith('/web/settings');
};

// VAIN “Muut” (Other) -sivu
const isOtherSettingsPage = () => {
  const p = pathname();

  // 1) reitillä suoraan
  if (/(^|\/)(web\/)?settings\/preferences\/other(\/|$)/.test(p)) return true;

  // 2) SPA/SSR: aktiivinen navigaatio-linkki
  const activeSel = [
    '.simple-navigation a[aria-current="page"]',
    '.settings-nav a[aria-current="page"]',
    '.tabs-nav a[aria-current="page"]',
    'nav a[aria-current="page"]',
  ].join(',');

  const active = document.querySelector(activeSel);
  const label  = (active?.textContent || '').trim().toLowerCase();

  if (/^(muut|other)$/.test(label)) return true;

  // 3) jos navissa löytyy linkki, joka viittaa "/preferences/other", käytä myös sitä
  const otherLink = document.querySelector('a[href*="/settings/preferences/other"], a[href*="/web/settings/preferences/other"]');
  if (otherLink && otherLink.getAttribute('aria-current') === 'page') return true;

  // 4) kehitysapu: aseta käsin konsolista window.__forceOther = true
  if (window.__forceOther === true) return true;

  return false;
};

// Pääkolumni (oikea paneeli)
const findSettingsMain = () =>
  document.querySelector('.columns-area__panels__main') ||
  document.querySelector('.columns-area__panels > .column:last-child') ||
  document.querySelector('.settings') ||
  document.querySelector('#mastodon main') ||
  document.body;

/* ------------- Asetuslohkon upotus “Muut”-sivun alkuun ------------- */
function tryMountPreferencesCard() {
  // näkyy vain Asetukset → Muut
  const isSettings = (() => {
    const p = location.pathname || '/';
    return p.startsWith('/settings') || p.startsWith('/web/settings');
  })();
  const isOther = (() => {
    const p = location.pathname || '/';
    if (/(^|\/)(web\/)?settings\/preferences\/other(\/|$)/.test(p)) return true;
    const active = document.querySelector('.simple-navigation a[aria-current="page"], .settings-nav a[aria-current="page"]');
    const label = (active?.textContent || '').trim().toLowerCase();
    return /^(muut|other)$/.test(label);
  })();

  if (!isSettings || !isOther) return;

  let mounted = false;
  const started = Date.now();

  // Hae “Muut”-sivun lomake ja sen kortin runko
  const findOtherFormContainer = () => {
    const form =
      document.querySelector('form[action*="/settings/preferences/other"]') ||
      document.querySelector('form[action*="/web/settings/preferences/other"]');
    if (form) {
      const body = form.closest('.card')?.querySelector('.card__body') || form;
      return body;
    }
    // fallback: pääkolumni
    return (
      document.querySelector('.columns-area__panels__main') ||
      document.querySelector('.columns-area__panels > .column:last-child') ||
      document.querySelector('.settings') ||
      document.querySelector('#mastodon main') ||
      document.body
    );
  };

  const mountOnce = () => {
    if (mounted) return;
    if (Date.now() - started > 20000) return;

    const container = findOtherFormContainer();
    if (!container) return;

    // Luo host ja työnnä se LOMAKKEEN alkuun (ensimmäiseksi kentäksi)
    let host = container.querySelector('#auto-load-images-inline');
    if (!host) {
      host = document.createElement('div');
      host.id = 'auto-load-images-inline';
      host.className = 'fields-group';
      container.insertBefore(host, container.firstChild || null);
    }

    const root = createRoot(host);
    root.render(<AutoLoadImagesSetting />);
    mounted = true;
    if (window.console && console.info) console.info('[autoload] mounted inside Other form body');
  };

  // Havainnoi DOMin valmistumista
  const mo = new MutationObserver(() => {
    try {
      // jos poistuttiin Muut-sivulta, lopeta
      const p = location.pathname || '/';
      if (!/(^|\/)(web\/)?settings\/preferences\/other(\/|$)/.test(p)) { mo.disconnect(); return; }
      mountOnce();
      if (mounted) mo.disconnect();
    } catch (e) {
      console.warn('[autoload] mount fail', e);
      mo.disconnect();
    }
  });

  mo.observe(document.body, { childList: true, subtree: true });

  setTimeout(mountOnce, 0);
  setTimeout(() => { if (!mounted) mountOnce(); }, 600);
}

/* ----------- Näkymätön automaattinen liittäminen composerissa ----------- */

function startAutoAttach() {
  let mounted = false;

  const mountIfComposer = () => {
    if (mounted) return;
    const hasComposer =
      document.querySelector('.compose-form__buttons') ||
      document.querySelector('form.compose-form');

    if (!hasComposer) return;

    mounted = true;
    const host = document.createElement('div');
    host.id = 'paxsudos-auto-attach-host';
    host.style.display = 'none';
    document.body.appendChild(host);
    const root = createRoot(host);
    root.render(<AutoAttachByUrl />);
    dlog('auto-attach mounted (composer present)');
  };

  const mo = new MutationObserver(() => {
    try { mountIfComposer(); } catch (e) { console.warn('[autoload] auto-attach mount-virhe', e); }
  });
  mo.observe(document.body, { childList: true, subtree: true });

  try { mountIfComposer(); } catch (e) { console.warn('[autoload] auto-attach init-virhe', e); }
}

/* ---- (valinnainen) inline-kytkin, oletuksena pois käytöstä ---- */
function tryMountComposeInlineToggle() {
  if (!isSettingsPage()) return;
  const mount = () => {
    const toolbar = document.querySelector('.compose-form__buttons');
    if (!toolbar) return;
    if (document.getElementById('auto-load-inline-toggle')) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.id = 'auto-load-inline-toggle';
    btn.className = 'compose-form__button';
    btn.style.marginLeft = '6px';

    const setLabel = () => {
      const on = getEffective();
      btn.textContent = on ? 'Auto upotus: ON' : 'Auto upotus: OFF';
      btn.style.opacity = on ? '1' : '0.6';
      btn.title = on
        ? 'Automaattinen liittäminen on päällä'
        : 'Automaattinen liittäminen on pois päältä';
    };
    setLabel();

    btn.addEventListener('click', () => {
      setOverride(!getEffective());
      setLabel();
    });

    toolbar.appendChild(btn);

    const form = document.querySelector('form.compose-form');
    if (form && !form.dataset.autoLoadClearHook) {
      form.addEventListener('submit', () => clearOverride());
      form.dataset.autoLoadClearHook = '1';
    }
  };

  const mo = new MutationObserver(mount);
  mo.observe(document.body, { childList: true, subtree: true });
  mount();
}

/* --------------------------- boot --------------------------- */

document.addEventListener('DOMContentLoaded', () => {
  try { if (isOtherSettingsPage()) tryMountPreferencesCard(); } catch (e) { console.warn('[autoload] asetuslohkon mount epäonnistui', e); }
  // Inline-kytkintä ei aktivoida oletuksena:
  // try { if (isSettingsPage()) tryMountComposeInlineToggle(); } catch (e) {}
  try { startAutoAttach(); } catch (e) { console.warn('[autoload] auto-attach start epäonnistui', e); }
});

// reagoi SPA-reititykseen (pathname vaihtuu ilman reloadia)
(() => {
  let last = pathname();
  const tick = () => {
    const now = pathname();
    if (now !== last) {
      last = now;
      dlog('route changed →', now);
      if (isOtherSettingsPage()) {
        try { tryMountPreferencesCard(); } catch {}
      }
    }
  };
  setInterval(tick, 800);
})();
